import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

interface FitnessRingProps {
  value: number;
  max: number;
  label: string;
  delta: number;
  color: string;
  icon?: React.ReactNode;
  format?: (value: number) => string;
  size?: "sm" | "md" | "lg";
}

export function FitnessRing({
  value,
  max,
  label,
  delta,
  color,
  icon,
  format = (v) => v.toString(),
  size = "md",
}: FitnessRingProps) {
  // Honor the user's prefers-reduced-motion: skip the ring sweep + value
  // fade-in when motion is reduced. The numeric value is rendered either
  // way so the SR experience is unaffected.
  const reduceMotion = useReducedMotion();
  const percentage = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const sizeClasses = {
    sm: { ring: "w-20 h-20", text: "text-lg", label: "text-xs" },
    md: { ring: "w-28 h-28", text: "text-xl", label: "text-sm" },
    lg: { ring: "w-36 h-36", text: "text-2xl", label: "text-base" },
  };

  // Compose a single SR description for the whole ring so the user hears
  // "Leads Created: 6 (30% of goal 20), up 4% vs prior period" instead of a
  // bare number with a colored arrow.
  const pctOfGoal = Math.round(percentage);
  const deltaPhrase =
    delta === 0
      ? "no change vs prior period"
      : delta > 0
      ? `up ${Math.abs(delta)}% vs prior period`
      : `down ${Math.abs(delta)}% vs prior period`;
  const ringAriaLabel = `${label}: ${format(value)} (${pctOfGoal}% of goal ${format(max)}), ${deltaPhrase}`;

  return (
    <div
      className="flex flex-col items-center gap-2"
      role="group"
      aria-label={ringAriaLabel}
    >
      <div className={cn("relative", sizeClasses[size].ring)} aria-hidden="true">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r="40"
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            className="text-muted/30"
          />
          <motion.circle
            cx="50"
            cy="50"
            r="40"
            stroke={color}
            strokeWidth="8"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            initial={reduceMotion ? false : { strokeDashoffset: circumference }}
            animate={{ strokeDashoffset }}
            transition={reduceMotion ? { duration: 0 } : { duration: 1, ease: "easeOut" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {icon && <div className="mb-0.5 opacity-70">{icon}</div>}
          <motion.span
            className={cn("font-semibold", sizeClasses[size].text)}
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={reduceMotion ? { duration: 0 } : { delay: 0.5 }}
          >
            {format(value)}
          </motion.span>
        </div>
      </div>
      <div className="text-center" aria-hidden="true">
        <p className={cn("font-medium text-muted-foreground", sizeClasses[size].label)}>
          {label}
        </p>
        <DeltaBadge delta={delta} />
      </div>
    </div>
  );
}

function DeltaBadge({ delta }: { delta: number }) {
  if (delta === 0) {
    return <span className="text-xs text-muted-foreground">No change</span>;
  }

  const isPositive = delta > 0;
  // Color + arrow are decorative — the wrapping ring already carries an
  // aria-label that describes the delta in words ("up 4% vs prior period"),
  // so the badge itself is hidden from SR via the aria-hidden parent.
  return (
    <span
      className={cn(
        "text-xs font-medium",
        isPositive ? "text-green-500" : "text-red-500"
      )}
    >
      {isPositive ? "↑" : "↓"} {Math.abs(delta)}%
    </span>
  );
}

interface FitnessRingCardProps {
  metrics: Array<{
    key: string;
    value: number;
    delta: number;
    label: string;
    color: string;
    goal?: number;
    icon?: React.ReactNode;
    format?: (value: number) => string;
  }>;
  isLoading?: boolean;
}

export function FitnessRingCard({ metrics, isLoading }: FitnessRingCardProps) {
  if (isLoading) {
    return (
      <div className="flex flex-wrap justify-center gap-6 p-6">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex flex-col items-center gap-2">
            <div className="w-28 h-28 rounded-full bg-muted animate-pulse" />
            <div className="h-4 w-16 bg-muted rounded animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap justify-center gap-6 p-4">
      {metrics.map((metric) => (
        <FitnessRing
          key={metric.key}
          value={metric.value}
          max={metric.goal || Math.max(metric.value, 10)}
          label={metric.label}
          delta={metric.delta}
          color={metric.color}
          icon={metric.icon}
          format={metric.format}
        />
      ))}
    </div>
  );
}
